import React, { memo, useMemo, useState, useCallback } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { formatRoute } from "react-router-named-routes";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import _cloneDeep from "lodash/cloneDeep";
import { PRIVATE_ROUTES } from "app/routers/Router.config";
import { GoogleMap } from "app/components/GoogleMap/GoogleMap";
import {
  DashboardFlowMarker,
  DashboardApiV2DashboardFlowMarkersGetRequest,
  GroupedTestResult,
  TestRecordDTO,
  TestResult,
  GroupedTestRecordsApiV2GroupedTestRecordsIdGetRequest,
  GroupedTestRecordDTO,
  TestRecordsApiV2TestRecordsIdGetRequest,
} from "@generated/v2";
import { useFetchData } from "app/hooks/hooks";
import { dashboardMarkersGet } from "app/api/v2/dashboardApi";
import DashboardItemLayout from "../DashboardItemLayout/DashboardItemLayout";
import { DashboardItemFilterComplete } from "app/types/dashboardType";
import styles from "./DashboardMap.module.scss";
import Icon from "app/components/Icon/Icon";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import { IconProps } from "antd/lib/icon";
import { ChartTestFlowToggleLabel } from "app/screens/DashboardScreen/components/common/ChartTestFlowToggleLabel";
import { SwitchProps } from "app/components/Common/Switch";
import TestRecordDetails from "app/components/TestRecordDetails/TestRecordDetails";
import { DetailsParams } from "app/types/routerType";
import { groupedTestRecordGet } from "app/api/v2/groupedTestRecordsApi";
import { testRecordGet } from "app/api/v2/testRecordsApi";

function groupedResultToTestResult(result: GroupedTestResult): TestResult {
  switch (result) {
    case GroupedTestResult.Positive:
      return TestResult.Positive;
    case GroupedTestResult.Negative:
      return TestResult.Negative;
    case GroupedTestResult.Inconclusive:
      return TestResult.WeakPositive;
  }
}

const DashboardMap = ({
  customerId,
  siteId,
  start,
  end,
  routes,
  period,
}: DashboardItemFilterComplete) => {
  const { t } = useTranslation();

  const location = useLocation();
  const { itemId } = useParams<DetailsParams>();

  const [showModal, setShowModal] = useState(false);
  const [showNegative, setShowNegative] = useState(true);
  const [showPositive, setShowPositive] = useState(true);

  const history = useHistory();
  const detailsPath =
    PRIVATE_ROUTES.DASHBOARD_MAP_TEST_RECORD_DETAILS_SCREEN.path;
  const dashboardPath = PRIVATE_ROUTES.DASHBOARD_SCREEN.path;

  const { data, loading } = useFetchData<
    DashboardFlowMarker[],
    DashboardApiV2DashboardFlowMarkersGetRequest
  >(dashboardMarkersGet, {
    shouldCallApi: !!customerId,
    params: {
      customerId,
      siteId,
      start,
      end,
      routes,
    },
  });

  const { data: groupedTestRecord, loading: groupedTestLoading } = useFetchData<
    GroupedTestRecordDTO,
    GroupedTestRecordsApiV2GroupedTestRecordsIdGetRequest
  >(groupedTestRecordGet, {
    shouldCallApi: !!itemId,
    params: {
      id: parseInt(itemId),
    },
  });

  const testRecordId = useMemo(
    () => groupedTestRecord?.testRecords?.[0]?.id,
    [groupedTestRecord]
  );

  const { data: testRecord, loading: testRecordLoading } = useFetchData<
    TestRecordDTO,
    TestRecordsApiV2TestRecordsIdGetRequest
  >(testRecordGet, {
    shouldCallApi: !!testRecordId,
    params: {
      id: testRecordId,
    },
  });

  const mapData = useMemo(() => {
    const dataClone = _cloneDeep(data);

    /* filter first for results, 
      and then a second time for invalid locations 
      in order to make map "useSuperCluster" hook work in GoogleMap.tsx 
      (Doing the filtering here takes off load from GoogleMap.tsx that otherwise should be filtering two times)
      */
    const filteredData = dataClone?.filter(
      data =>
        (showPositive && data.result === GroupedTestResult.Positive) ||
        (showPositive && data.result === GroupedTestResult.Inconclusive) ||
        (showNegative && data.result === GroupedTestResult.Negative)
    );

    return filteredData
      ?.map((item, index) => {
        if (!item.location) {
          filteredData[index].location = { latitude: 0, longitude: 0 };
        }
        return filteredData[index];
      })
      .map<TestRecordDTO>(item => ({
        ...item,
        result: groupedResultToTestResult(item.result),
      }));
  }, [data, showNegative, showPositive]);

  /**
   * Handles redirects, which triggers modal visibility
   * @param path The path to redirect to, usually associated to a given modal
   */
  const redirectTo = (path: string, id?: number) => {
    const newRoute = formatRoute(path, {
      itemId: id,
    });
    history.push({ pathname: newRoute, search: location.search });
  };

  const togglePositive = useCallback(() => {
    setShowPositive(prevState => !prevState);
  }, []);

  const toggleNegative = useCallback(() => {
    setShowNegative(prevState => !prevState);
  }, []);

  const toggleFullscreenMap = useCallback(() => {
    setShowModal(prevState => !prevState);
  }, []);

  const icons = useMemo<IconProps[]>(() => {
    return [
      {
        type: "fullscreen",
        onClick: toggleFullscreenMap,
      },
    ];
  }, [toggleFullscreenMap]);

  const switches = useMemo<SwitchProps[]>(
    () => [
      {
        checked: showPositive,
        onChange: togglePositive,
        label: (
          <ChartTestFlowToggleLabel
            i18nKey="dashboard.checkboxShowPositiveFlowsWithBox"
            positive
          />
        ),
      },
      {
        checked: showNegative,
        onChange: toggleNegative,
        label: (
          <ChartTestFlowToggleLabel
            i18nKey="dashboard.checkboxShowNegativeFlowsWithBox"
            positive={false}
          />
        ),
      },
    ],
    [showNegative, showPositive, toggleNegative, togglePositive]
  );

  return (
    <ErrorFallbackUI>
      <DashboardItemLayout
        background
        title={t("dashboard.mapTitle")}
        subTitle={period}
        loading={loading}
        icons={icons}
        info={t("dashboard.mapInfo")}
        switches={switches}
      >
        {mapData && (
          <>
            <GoogleMap
              data={mapData ?? []}
              className={cx(styles.container, { [styles.full]: showModal })}
              onClickMarker={id => {
                redirectTo(detailsPath, id);
              }}
            />
            <Icon
              type="fullscreen-exit"
              onClick={toggleFullscreenMap}
              className={cx(styles.closeMap, { [styles.active]: showModal })}
            />
          </>
        )}
      </DashboardItemLayout>

      {!!itemId &&
        !!testRecord &&
        !testRecordLoading &&
        !groupedTestLoading && (
          <TestRecordDetails
            data={testRecord}
            show={true}
            close={() => redirectTo(dashboardPath)}
            showMap
          />
        )}
    </ErrorFallbackUI>
  );
};

export default memo(DashboardMap);
